import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdAccountBalanceWallet, MdCheckCircle, MdHourglassEmpty, MdInfoOutline } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';
import { BankTab } from './ArtistTabs';

// Pagamentos: what the Izenu owes this distributor for its artists — 70% of
// their sales and the streaming royalties of closed months — and the
// withdrawal requests. Only for distributors with payout_to = 'distributor';
// the Izenu pays the others' artists directly (Estúdio → Ganhos).

const fmtMoney = (amount, currency) => {
    const n = Number(amount) || 0;
    if (currency === 'AOA') return `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(n)} Kz`;
    if (currency === 'EUR' || currency === 'USD') return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(n);
    return `${new Intl.NumberFormat('pt-PT').format(n)} ${currency}`;
};
const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date(String(s).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
};
const PAYOUT_METHOD = { bank_transfer: 'Transferência bancária', multicaixa_express: 'Multicaixa Express', other: 'Outro' };
const STATUS = {
    pending: ['Em análise', 'text-amber-700'],
    paid: ['Pago', 'text-green-700'],
    rejected: ['Não aprovado', 'text-red-600'],
    cancelled: ['Cancelado', 'text-gray-500'],
};
const card = 'rounded-xl border border-gray-200 bg-white';
const th = 'px-4 py-2.5 font-semibold';

function BalanceCard({ b, pending, onRequest, onCancel, busy, onGoToAccount }) {
    const min = Number(b.min || 0);
    const progress = min > 0 ? Math.min(100, Math.max(0, (b.available / min) * 100)) : 0;
    return (
        <div className={`${card} flex flex-col p-4`}>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <MdAccountBalanceWallet className="text-amber-500" size={16} /> Disponível para levantar
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{fmtMoney(b.available, b.currency)}</p>
            {b.currency !== 'AOA' && <p className="text-xs text-gray-500">Pago em Kz, ao câmbio do dia do pagamento.</p>}
            {/* How the available amount is made up, so the Royalties page total is not read as a second balance. */}
            {(b.royalties_earned > 0 || b.sales_earned > 0 || b.paid > 0 || b.pending > 0) && (
                <dl className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-left text-xs text-gray-600">
                    {b.royalties_earned > 0 && (
                        <div className="flex justify-between gap-3">
                            <dt><Link to="/royalties" className="font-semibold text-amber-700 hover:underline">Royalties de streaming</Link> (meses fechados)</dt>
                            <dd className="tabular-nums">{fmtMoney(b.royalties_earned, b.currency)}</dd>
                        </div>
                    )}
                    {b.sales_earned > 0 && <div className="flex justify-between gap-3"><dt><Link to="/sales-report" className="font-semibold text-amber-700 hover:underline">Vendas de álbuns</Link> (parte dos artistas)</dt><dd className="tabular-nums">{fmtMoney(b.sales_earned, b.currency)}</dd></div>}
                    {b.paid > 0 && <div className="flex justify-between gap-3"><dt>Já pago</dt><dd className="tabular-nums">− {fmtMoney(b.paid, b.currency)}</dd></div>}
                    {b.pending > 0 && <div className="flex justify-between gap-3"><dt>Pedido em análise</dt><dd className="tabular-nums">− {fmtMoney(b.pending, b.currency)}</dd></div>}
                    <div className="flex justify-between gap-3 border-t border-gray-100 pt-1 font-semibold text-gray-900"><dt>Disponível</dt><dd className="tabular-nums">{fmtMoney(b.available, b.currency)}</dd></div>
                </dl>
            )}
            <div className="mt-auto pt-4">
                {b.can_request && (
                    <button type="button" disabled={busy} onClick={onRequest} className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-60">
                        {busy ? 'A enviar…' : 'Solicitar levantamento'}
                    </button>
                )}
                {b.reason === 'pending' && pending && (
                    <p className="text-sm text-gray-700">
                        <span className="flex items-center gap-1.5 font-semibold"><MdHourglassEmpty className="text-amber-500" /> Pedido de {fmtMoney(pending.amount, pending.currency)} em análise</span>
                        <span className="text-xs text-gray-500">Pedido a {fmtDate(pending.requested_at)} · </span>
                        <button type="button" onClick={() => onCancel(pending)} className="text-xs text-gray-600 underline">Cancelar pedido</button>
                    </p>
                )}
                {b.reason === 'below_minimum' && min > 0 && (
                    <div className="text-xs text-gray-600">
                        <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-amber-500" style={{ width: `${progress}%` }} /></div>
                        Faltam {fmtMoney(min - b.available, b.currency)} para poder levantar (mínimo {fmtMoney(min, b.currency)}).
                    </div>
                )}
                {(b.reason === 'no_account' || b.reason === 'incomplete_account') && (
                    <button type="button" onClick={onGoToAccount} className="text-sm font-semibold text-amber-700 hover:underline">
                        {b.reason === 'no_account' ? 'Indica os dados bancários da distribuidora' : 'Completa os dados bancários da distribuidora'}
                    </button>
                )}
            </div>
        </div>
    );
}

/** payout_to = 'artist': read-only view of the artists Izenu pays directly. */
function DirectArtists({ data }) {
    const artists = data.artists || [];
    const requests = data.artist_requests || [];
    const sum = (key) => artists.reduce((s, a) => s + Number((a.balances || []).find((b) => b.currency === 'AOA')?.[key] || 0), 0);
    const pendingTotal = requests.filter((r) => r.status === 'pending').reduce((s, r) => s + (r.currency === 'AOA' ? Number(r.amount) : 0), 0);
    const usdLines = (a, key) => (a.balances || []).filter((b) => b.currency !== 'AOA' && Number(b[key]) > 0).map((b) => fmtMoney(b[key], b.currency));
    const cell = (a, key) => {
        const kz = Number((a.balances || []).find((b) => b.currency === 'AOA')?.[key] || 0);
        return [kz > 0 ? fmtMoney(kz, 'AOA') : null, ...usdLines(a, key)].filter(Boolean).join(' + ') || '—';
    };

    return (
        <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className={`${card} p-4`}>
                    <p className="text-xs font-semibold uppercase text-gray-500">Ganho pelos artistas</p>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{fmtMoney(sum('earned'), 'AOA')}</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                        <Link to="/sales-report" className="text-amber-700 hover:underline">Vendas de álbuns</Link> {fmtMoney(sum('sales_earned'), 'AOA')} · <Link to="/royalties" className="text-amber-700 hover:underline">Royalties</Link> {fmtMoney(sum('royalties_earned'), 'AOA')}
                    </p>
                </div>
                <div className={`${card} p-4`}><p className="text-xs font-semibold uppercase text-gray-500">Já pago aos artistas</p><p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{fmtMoney(sum('paid'), 'AOA')}</p></div>
                <div className={`${card} p-4`}><p className="text-xs font-semibold uppercase text-gray-500">Pedidos em análise</p><p className="mt-1 text-2xl font-bold tabular-nums text-amber-700">{fmtMoney(pendingTotal, 'AOA')}</p></div>
            </div>

            <section>
                <h2 className="mb-2 text-base font-semibold text-gray-900">Por artista</h2>
                {artists.length === 0 ? <p className={`${card} p-4 text-sm text-gray-500`}>Ainda nenhum artista teve vendas, royalties ou pagamentos.</p> : (
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[820px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500"><th className={th}>Artista</th><th className={`${th} text-right`}>Vendas de álbuns</th><th className={`${th} text-right`}>Royalties de streaming</th><th className={`${th} text-right`}>Já pago</th><th className={`${th} text-right`}>Saldo</th><th className={th}>Situação</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {artists.map((a) => (
                                    <tr key={a.artist_id}>
                                        <td className="px-4 py-2.5"><Link to={`/artist/${a.artist_id}?tab=earnings`} className="text-gray-900 hover:underline">{a.name || `#${a.artist_id}`}</Link></td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{cell(a, 'sales_earned')}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{cell(a, 'royalties_earned')}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{cell(a, 'paid')}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{cell(a, 'balance')}</td>
                                        <td className="px-4 py-2.5 text-xs">
                                            {a.pending_request ? <span className="font-semibold text-amber-700">Pedido em análise</span>
                                                : !a.has_account ? <span className="text-gray-500">Sem dados bancários</span>
                                                : !a.account_complete ? <span className="text-gray-500">Dados bancários incompletos</span>
                                                : <span className="text-gray-500">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {requests.length > 0 && (
                <section>
                    <h2 className="mb-2 text-base font-semibold text-gray-900">Pedidos de levantamento dos artistas</h2>
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[520px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500"><th className={th}>Data</th><th className={th}>Artista</th><th className={th}>Estado</th><th className={`${th} text-right`}>Valor</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {requests.map((r) => (
                                    <tr key={r.id} className="align-top">
                                        <td className="px-4 py-2.5 text-gray-500">{fmtDate(r.requested_at)}</td>
                                        <td className="px-4 py-2.5 text-gray-900">{r.artist_name}</td>
                                        <td className="px-4 py-2.5"><span className={`font-semibold ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span>{r.status === 'rejected' && r.note && <div className="text-xs text-gray-500">{r.note}</div>}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(r.amount, r.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

        </>
    );
}

export default function Payouts() {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState('');
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState('artists');

    const load = useCallback(() => {
        izenuAPI.get('/distributor/payouts/summary.php')
            .then(({ data }) => setData(data))
            .catch((e) => setFailed(e?.response?.data?.message || 'Não foi possível carregar os pagamentos.'));
    }, []);
    useEffect(() => { load(); }, [load]);

    const request = async (b) => {
        const inKz = b.currency !== 'AOA' ? ' (pago em Kz, ao câmbio do dia do pagamento)' : '';
        if (!window.confirm(`Pedir o levantamento de ${fmtMoney(b.available, b.currency)}${inKz} para os dados bancários da distribuidora?`)) return;
        setBusy(true);
        try {
            await izenuAPI.post('/distributor/payouts/request.php', { currency: b.currency });
            load();
        } catch (e) {
            window.alert(e?.response?.data?.message || 'Não foi possível enviar o pedido.');
        }
        setBusy(false);
    };
    const cancel = async (r) => {
        if (!window.confirm(`Cancelar o pedido de ${fmtMoney(r.amount, r.currency)}?`)) return;
        try {
            await izenuAPI.post('/distributor/payouts/request.php', { cancel: r.id });
            load();
        } catch (e) {
            window.alert(e?.response?.data?.message || 'Não foi possível cancelar.');
        }
    };

    if (failed) return <div className="p-6"><p className="text-sm text-red-600">{failed}</p></div>;
    if (!data) return <div className="p-6"><p className="text-sm text-gray-500">A carregar…</p></div>;

    const pct = Number(data.share_pct || 70);
    const direct = data.payout_to === 'artist';
    const minAoa = Number(data.min_withdrawal?.AOA || 20000);
    // With nothing earned yet, still show the Kz card: 0, the bar to the minimum, the rule.
    const balances = (data.balances || []).length ? data.balances : [{
        currency: 'AOA', available: 0, sales_earned: 0, royalties_earned: 0, paid: 0, min: minAoa,
        can_request: false, reason: 'below_minimum',
    }];
    const pendingBy = Object.fromEntries((data.requests || []).filter((r) => r.status === 'pending').map((r) => [r.currency, r]));

    const artists = data.by_artist || [];
    // One history of withdrawals: each request with its payment, plus any payment
    // the team recorded without a request.
    const payoutById = Object.fromEntries((data.payouts || []).map((p) => [p.id, p]));
    const withdrawals = [
        ...(data.requests || []).map((r) => ({ key: `r${r.id}`, date: r.requested_at, request: r, payout: r.payout_id ? payoutById[r.payout_id] : null })),
        ...(data.payouts || []).filter((p) => !p.request_id).map((p) => ({ key: `p${p.id}`, date: p.paid_at, request: null, payout: p })),
    ].sort((x, y) => String(y.date).localeCompare(String(x.date)));

    const tabs = [['artists', 'Por artista'], ['withdrawals', 'Levantamentos'], ['account', 'Dados bancários']];

    return (
        <div className="space-y-6 p-4 text-left sm:p-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Pagamentos</h1>
                <p className="mt-1 text-sm text-gray-600">O total a receber, somando as vendas de álbuns e os royalties de streaming dos artistas, e os levantamentos.</p>
            </div>

            <div className={`${card} flex items-start gap-3 p-4 text-sm text-gray-600`}>
                <MdInfoOutline className="mt-0.5 shrink-0 text-amber-600" size={18} />
                {direct ? (
                    <p>A Izenu paga <b className="text-gray-900">directamente aos artistas</b> desta distribuidora: cada um pede o levantamento no Estúdio a partir de {fmtMoney(minAoa, 'AOA')}, e a equipa da Izenu faz o pagamento. Aqui fica a visão de todos, só para consulta: o que cada artista ganhou ({pct}% das vendas e royalties dos meses fechados), o que já recebeu e os pedidos em análise.</p>
                ) : (
                    <p>
                        A Izenu paga à distribuidora a parte dos seus artistas: <b className="text-gray-900">{pct}% das vendas de álbuns</b> (desde {fmtDate(data.since)})
                        e os <b className="text-gray-900">royalties de streaming</b> dos meses fechados. A distribuidora paga aos artistas conforme os vossos contratos.
                        Pode pedir o levantamento a partir de {fmtMoney(minAoa, 'AOA')}.
                    </p>
                )}
            </div>

            {direct && <DirectArtists data={data} />}

            {!direct && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {balances.map((b) => (
                        <BalanceCard key={b.currency} b={b} pending={pendingBy[b.currency]} busy={busy}
                            onRequest={() => request(b)} onCancel={cancel} onGoToAccount={() => setTab('account')} />
                    ))}
                </div>
            )}

            {!direct && (
                <>
                    <div className="flex flex-wrap gap-2 border-b border-gray-200">
                        {tabs.map(([k, t]) => (
                            <button key={k} type="button" onClick={() => setTab(k)}
                                className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === k ? 'border-amber-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t}</button>
                        ))}
                    </div>

                    {tab === 'artists' && (
                        artists.length === 0 ? <p className={`${card} p-4 text-sm text-gray-500`}>Ainda nenhum artista teve vendas ou royalties.</p> : (
                            <>
                                <p className="text-xs text-gray-500">Quanto cabe a cada artista, para a distribuidora lhe pagar conforme o vosso contrato.</p>
                                <div className={`${card} overflow-x-auto`}>
                                    <table className="w-full min-w-[620px] text-sm">
                                        <thead><tr className="text-left text-xs uppercase text-gray-500">
                                            <th className={th}>Artista</th>
                                            <th className={`${th} text-right`}>Vendas de álbuns ({pct}%)</th>
                                            <th className={`${th} text-right`}>Royalties de streaming</th>
                                            <th className={`${th} text-right`}>Total</th>
                                        </tr></thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {artists.map((a) => {
                                                const salesKz = Number(a.sales.AOA || 0);
                                                const others = Object.entries(a.sales).filter(([c]) => c !== 'AOA');
                                                return (
                                                    <tr key={a.artist_id}>
                                                        <td className="px-4 py-2.5"><Link to={`/artist/${a.artist_id}?tab=earnings`} className="text-gray-900 hover:underline">{a.name || `#${a.artist_id}`}</Link></td>
                                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                                                            {salesKz > 0 || !others.length ? (salesKz > 0 ? fmtMoney(salesKz, 'AOA') : '—') : null}
                                                            {others.map(([c, v]) => <div key={c} className="text-xs text-gray-500">+ {fmtMoney(v, c)}</div>)}
                                                        </td>
                                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{a.royalties_aoa > 0 ? fmtMoney(a.royalties_aoa, 'AOA') : '—'}</td>
                                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(salesKz + Number(a.royalties_aoa), 'AOA')}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )
                    )}

                    {tab === 'withdrawals' && (
                        withdrawals.length === 0 ? <p className={`${card} p-4 text-sm text-gray-500`}>Ainda não pediu nenhum levantamento.</p> : (
                            <div className={`${card} overflow-x-auto`}>
                                <table className="w-full min-w-[640px] text-sm">
                                    <thead><tr className="text-left text-xs uppercase text-gray-500">
                                        <th className={th}>Pedido</th><th className={th}>Estado</th><th className={th}>Pagamento</th><th className={`${th} text-right`}>Valor</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {withdrawals.map(({ key, request: r, payout: p }) => (
                                            <tr key={key} className="align-top">
                                                <td className="px-4 py-2.5 text-gray-500">
                                                    {r ? fmtDate(r.requested_at) : <span className="text-gray-400">Sem pedido</span>}
                                                    {(r?.account_snapshot || p?.account_snapshot) && <div className="text-xs text-gray-400">{r?.account_snapshot || p?.account_snapshot}</div>}
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    {r ? <span className={`font-semibold ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span> : <span className="font-semibold text-green-700">Pago</span>}
                                                    {r?.status === 'rejected' && r.note && <div className="text-xs text-gray-500">{r.note}</div>}
                                                    {r?.status === 'pending' && <div><button type="button" onClick={() => cancel(r)} className="text-xs text-gray-600 underline">Cancelar pedido</button></div>}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-600">
                                                    {p ? <><MdCheckCircle className="mr-1 inline text-green-600" />{fmtDate(p.paid_at)} · {PAYOUT_METHOD[p.method] || p.method}{p.reference && <div className="text-xs text-gray-400">Ref. {p.reference}</div>}</> : '—'}
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(r ? r.amount : p.amount, r ? r.currency : p.currency)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    )}

                    {tab === 'account' && <BankTab own />}
                </>
            )}
        </div>
    );
}
